/**
 * Hook para gravação de áudio do chat.
 *
 * ESTRATÉGIA (robusta, sem áudio picotado):
 *  - Caminho preferencial: polyfill `opus-media-recorder` (OGG/Opus) — gera
 *    arquivo compatível com WhatsApp (Z-API / WAHA / Meta WABA) e exibe
 *    waveform no destinatário.
 *  - Fallback: `MediaRecorder` NATIVO com Opus quando o polyfill não
 *    carregar (ex.: erro de bundler, navegador travado).
 *  - `mediaRecorder.start()` é chamado SEM timeslice — usar `start(100)`
 *    com o polyfill produzia múltiplos containers OGG concatenados, com
 *    páginas Ogg mal alinhadas, gerando o áudio "picotado".
 *  - `getUserMedia` não força `sampleRate` (o encoder Opus reamostra
 *    internamente). Captura em mono com EC/NS/AGC, padrão WhatsApp.
 *  - Timer de duração baseado em `Date.now()` (independe do gravador).
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  detectNativeOpusMime,
  loadOpusPolyfill,
  type OpusWorkerOptions,
} from '@/lib/opusRecorder';
import { createLogger } from '@/lib/logger';

// Logger para gravação de áudio (só aparece em desenvolvimento)
const audioLogger = createLogger('AudioRecorder');

interface UseAudioRecorderReturn {
  // Estado da gravação
  isRecording: boolean;
  // Duração em segundos
  duration: number;
  // Blob do áudio gravado (disponível após parar)
  audioBlob: Blob | null;
  // URL temporária para preview (disponível após parar)
  audioUrl: string | null;
  // Inicia gravação
  startRecording: () => Promise<boolean>;
  // Para gravação e retorna o Blob
  stopRecording: () => Promise<Blob | null>;
  // Cancela gravação sem retornar dados
  cancelRecording: () => void;
  // Limpa o áudio gravado
  clearRecording: () => void;
  // Erro se houver (erros de runtime, não de inicialização)
  error: string | null;
  // Se o gravador está pronto para uso
  isReady: boolean;
  // Se houve falha crítica na inicialização do gravador
  initFailed: boolean;
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [initFailed, setInitFailed] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  // Construtor + workerOptions do polyfill (carregados lazy).
  const polyfillCtorRef = useRef<any | null>(null);
  const polyfillOptionsRef = useRef<OpusWorkerOptions | null>(null);
  // Mimetype nativo escolhido como fallback (null = usar polyfill).
  const nativeMimeRef = useRef<string | null>(null);
  const isInitializingRef = useRef(false);

  // Inicialização: tenta polyfill (OGG/Opus) PRIMEIRO; se falhar, usa nativo.
  useEffect(() => {
    const init = async () => {
      if (isInitializingRef.current) return;
      isInitializingRef.current = true;

      try {
        const { ctor, options } = await loadOpusPolyfill();
        polyfillCtorRef.current = ctor;
        polyfillOptionsRef.current = options;
        nativeMimeRef.current = null;
        setIsReady(true);
        audioLogger.info('Gravador pronto (polyfill OGG/Opus) — compatível com WhatsApp');
      } catch (polyfillErr) {
        audioLogger.warn('Polyfill OGG/Opus indisponível — tentando nativo', polyfillErr);
        try {
          const native = detectNativeOpusMime();
          if (!native) throw new Error('Nenhum MediaRecorder Opus disponível');
          nativeMimeRef.current = native;
          setIsReady(true);
          audioLogger.info('Usando MediaRecorder NATIVO (fallback)', { mimeType: native });
        } catch (nativeErr) {
          audioLogger.error('Falha CRÍTICA ao inicializar gravador', nativeErr);
          setInitFailed(true);
          setIsReady(false);
        }
      }
    };

    init();
  }, []);

  // Limpa recursos ao desmontar
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const startRecording = useCallback(async (): Promise<boolean> => {
    if (!isReady) {
      setError('Aguarde, preparando gravador...');
      return false;
    }

    try {
      setError(null);

      // Captura otimizada para voz (mono, padrão WhatsApp).
      // Sem forçar sampleRate — o encoder Opus reamostra internamente para 48 kHz.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });

      streamRef.current = stream;
      chunksRef.current = [];

      let mediaRecorder: MediaRecorder;
      const recorderOptions: MediaRecorderOptions = {
        audioBitsPerSecond: 32000, // Voz com qualidade boa e arquivo enxuto
      };

      if (polyfillCtorRef.current && polyfillOptionsRef.current) {
        // CAMINHO PREFERENCIAL — polyfill OGG/Opus
        const PolyMR: any = polyfillCtorRef.current;
        mediaRecorder = new PolyMR(
          stream,
          { mimeType: 'audio/ogg;codecs=opus', ...recorderOptions },
          polyfillOptionsRef.current,
        );
        audioLogger.debug('Gravando via POLYFILL OGG/Opus (WhatsApp-ready)');
      } else if (nativeMimeRef.current) {
        // FALLBACK — MediaRecorder nativo
        const NativeMR: any = (window as any).MediaRecorder;
        mediaRecorder = new NativeMR(stream, {
          mimeType: nativeMimeRef.current,
          ...recorderOptions,
        });
        audioLogger.debug('Gravando via NATIVO (fallback)', {
          mimeType: nativeMimeRef.current,
        });
      } else {
        setError('Gravador não inicializado. Recarregue a página.');
        stream.getTracks().forEach((t) => t.stop());
        return false;
      }

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // IMPORTANTE: start() SEM timeslice — produz um único container
      // contínuo no onstop, eliminando o "picotado" do polyfill com start(100).
      mediaRecorder.start();
      setIsRecording(true);
      startTimeRef.current = Date.now();

      // Timer baseado em Date.now (independente do gravador)
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setDuration(elapsed);
      }, 250);

      audioLogger.info('Gravação iniciada');
      return true;
    } catch (err) {
      audioLogger.error('Erro ao iniciar gravação', err);
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Permissão de microfone negada. Verifique as configurações do navegador.');
      } else {
        setError('Não foi possível acessar o microfone. Verifique as permissões.');
      }
      return false;
    }
  }, [isReady]);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }

      // Para o timer imediatamente; o resto acontece no onstop
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      recorder.onstop = () => {
        // Preserva o mimetype real gravado (ogg ou webm), normalizando para
        // o tipo base sem o sufixo `;codecs=...`.
        const recordedMime = recorder.mimeType || 'audio/ogg';
        const baseType = recordedMime.split(';')[0] || 'audio/ogg';
        const blob = new Blob(chunksRef.current, { type: baseType });

        setAudioBlob(blob);

        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        // Para tracks DEPOIS que o recorder terminou (evita corte do último frame)
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        setIsRecording(false);
        audioLogger.info('Gravação finalizada', { size: blob.size, type: baseType });

        resolve(blob);
      };

      try {
        recorder.stop();
      } catch (err) {
        audioLogger.error('Erro ao parar gravação', err);
        resolve(null);
      }
    });
  }, []);

  const cancelRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        // ignora — em alguns browsers stop após erro lança
      }
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    chunksRef.current = [];
    setIsRecording(false);
    setDuration(0);
    setAudioBlob(null);

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }

    audioLogger.debug('Gravação cancelada');
  }, [audioUrl]);

  const clearRecording = useCallback(() => {
    setAudioBlob(null);
    setDuration(0);

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  }, [audioUrl]);

  return {
    isRecording,
    duration,
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    cancelRecording,
    clearRecording,
    error,
    isReady,
    initFailed,
  };
}

/**
 * Formata segundos como duração legível (mm:ss).
 */
export function formatAudioDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
